import type {
  CodeToolSecretListing,
  CodeToolSecretsWrite,
} from "@repo/app-types/entities/tools/code";
import type { CustomToolParameter } from "@repo/app-types/entities/tools/custom";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import isEqual from "lodash-es/isEqual.js";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { ToolFormConfig } from "../formConfigs/baseFormConfig.js";

export interface BaseToolFormData<TSettings> {
  name: string;
  description: string;
  settings: TSettings;
}

/** The settings a save sends, and a code tool's secrets write beside them. */
export interface PreparedToolSettings<TSettings> {
  settings: TSettings;
  secrets?: CodeToolSecretsWrite;
}

/** What a save sends. */
export interface BaseToolFormSaveData<
  TSettings,
> extends PreparedToolSettings<TSettings> {
  name: string;
  description: string;
}

interface UseToolFormParams<TSettings, TFormSettings> {
  config: ToolFormConfig<TSettings, TFormSettings>;
  initialData: BaseToolFormData<TSettings> | undefined;
  /** A code tool's stored secrets; must keep its identity between renders. */
  initialSecrets: readonly CodeToolSecretListing[];
  onSave: (data: BaseToolFormSaveData<TSettings>) => Promise<void>;
}

export interface UseToolFormResult<TSettings> {
  isEditMode: boolean;
  isFormValid: boolean;
  hasChanges: boolean;
  isSaving: boolean;
  onSave: () => Promise<void>;
  onDiscard: () => void;
  renderForm: () => ReactNode;
  /**
   * The settings and secrets write as the form has them now, unsaved, or
   * `undefined` after showing the form's errors when they aren't valid yet.
   */
  prepareSettings: () => PreparedToolSettings<TSettings> | undefined;
  /**
   * Shows an API refusal on the fields it is about, as a failed save does.
   * Returns whether any field took it.
   */
  applySaveError: (error: unknown) => boolean;
  /** The parameters as the form has them now, for a tool that has them. */
  parameters: readonly CustomToolParameter[] | undefined;
}

export function useBaseToolForm<TSettings, TFormSettings>(
  params: UseToolFormParams<TSettings, TFormSettings>,
): UseToolFormResult<TSettings> {
  const { config, initialData, initialSecrets, onSave } = params;

  // Initial form values: original tool values from props
  const initialName = initialData?.name ?? "";
  const initialDescription = initialData?.description ?? "";
  const initialSettings = useMemo(
    () => config.initialize(initialData?.settings, initialSecrets),
    [config, initialData?.settings, initialSecrets],
  );

  // Changed form values: changes the user made compared to the original
  const [changedName, setChangedName] = useState<string | undefined>(undefined);
  const [changedDescription, setChangedDescription] = useState<
    string | undefined
  >(undefined);
  const [changedSettings, setChangedSettings] = useState<
    Partial<TFormSettings>
  >({});

  const discardChanges = useCallback(() => {
    setChangedName(undefined);
    setChangedDescription(undefined);
    setChangedSettings({});
  }, []);

  // Current form values: computed from initial + changed
  const name = changedName ?? initialName;
  const description = changedDescription ?? initialDescription;
  const settings = useMemo(
    () => ({
      ...initialSettings,
      ...changedSettings,
    }),
    [initialSettings, changedSettings],
  );

  // Sanitized form values: this is what we'll save.
  const sanitizedName = name.trim();
  const sanitizedDescription = description.trim();

  const isEditMode = Boolean(initialData);

  // Perform real-time validation that disables the save button
  const isFormValid =
    sanitizedName !== "" &&
    sanitizedDescription !== "" &&
    config.validate(settings);

  // Detect changes (only relevant in edit mode)
  const hasChanges = useMemo(() => {
    if (!isEditMode) {
      return false;
    }

    return (
      changedName !== undefined ||
      changedDescription !== undefined ||
      Object.keys(changedSettings).length !== 0
    );
  }, [isEditMode, changedName, changedDescription, changedSettings]);

  const handleSettingsChange = useCallback(
    (changedSettings: Partial<TFormSettings>) => {
      setChangedSettings((prev) => {
        const merged = { ...prev, ...changedSettings };
        return isEqual(prev, merged) ? prev : merged;
      });
    },
    [],
  );

  const [isSaving, setIsSaving] = useState(false);

  const prepareSettingsFrom = (
    formSettings: TFormSettings,
  ): PreparedToolSettings<TSettings> | undefined => {
    const result = config.save(formSettings);
    if (!result.isValid) {
      setChangedSettings((prev) => ({ ...prev, ...result.formSettings }));
      return undefined;
    }
    return { settings: result.settings, secrets: result.secrets };
  };

  const handlePrepareSettings = useEffectEvent(() =>
    prepareSettingsFrom(settings),
  );

  const handleApplySaveError = useEffectEvent((error: unknown): boolean => {
    const formSettings = config.readSaveError?.(error);
    if (!formSettings) return false;
    setChangedSettings((prev) => ({ ...prev, ...formSettings }));
    return true;
  });

  const parameters = config.readParameters?.(settings);

  const handleSave = useEffectEvent(async () => {
    if (!isFormValid) {
      return;
    }

    setIsSaving(true);
    try {
      // Formatted first, so what is saved is what the form then shows.
      const formatted = (await config.formatSettings?.(settings)) ?? {};
      if (Object.keys(formatted).length > 0) {
        handleSettingsChange(formatted);
      }

      // Perform just-in-time validation before saving, which may update the form with errors
      const prepared = prepareSettingsFrom({ ...settings, ...formatted });
      if (!prepared) {
        return;
      }

      await onSave({
        name: sanitizedName,
        description: sanitizedDescription,
        ...prepared,
      });

      // Clear changed state after successful save
      discardChanges();
    } catch (error) {
      // `onSave` reports the failure itself; keep what the user typed so they
      // can fix it (a name that is already taken, say) and save again.
      handleApplySaveError(error);
    } finally {
      setIsSaving(false);
    }
  });

  const handleDiscard = useEffectEvent(() => {
    discardChanges();
  });

  const renderForm = () => {
    const Form = config.Form;

    // Nothing is edited while a save is in flight: the save sends the form as
    // it stood, and a finished save resets the form, which would drop edits
    // made meanwhile. A disabled fieldset disables every native field in it.
    return (
      <fieldset disabled={isSaving} className="contents">
        <Form
          isEditMode={isEditMode}
          isReadOnly={isSaving}
          name={name}
          description={description}
          settings={settings}
          onNameChange={setChangedName}
          onDescriptionChange={setChangedDescription}
          onSettingsChange={handleSettingsChange}
        />
      </fieldset>
    );
  };

  return {
    isEditMode,
    isFormValid,
    hasChanges,
    isSaving,
    onSave: handleSave,
    onDiscard: handleDiscard,
    renderForm,
    prepareSettings: handlePrepareSettings,
    applySaveError: handleApplySaveError,
    parameters,
  };
}
